import { minutes, type Minutes } from '@bigint-project/core';

/**
 * 工期字串解析(MS Project 風格)。
 *
 * 支援單位(不分大小寫):
 * - `m`  分鐘
 * - `h`  小時(60 分鐘)
 * - `d`  工作天(480 分鐘,即 8 小時工作日)
 * - `w`  工作週(2400 分鐘,即 5 個工作天)
 * - `mo` 工作月(9600 分鐘,即 20 個工作天)
 *
 * 加上 `e` 前綴代表「彈性(elapsed)工期」:以連續 24 小時計算,不受行事曆工作時間限制。
 * - `eh` 60 分鐘、`ed` 1440 分鐘、`ew` 10080 分鐘、`emo` 43200 分鐘
 *
 * `md` 為本元件擴充語法(非 MS Project 原生),視為「人天(man-day)」,
 * 換算與 `d` 相同(480 分鐘),用來輸入依人力估算的工期。
 *
 * 數字可為整數或小數,例如 `2.5d`。省略單位時預設為 `d`。
 */

export const MINUTES_PER_HOUR = 60;
export const MINUTES_PER_DAY = 480;
export const MINUTES_PER_WEEK = MINUTES_PER_DAY * 5;
export const MINUTES_PER_MONTH = MINUTES_PER_DAY * 20;

export const ELAPSED_MINUTES_PER_HOUR = 60;
export const ELAPSED_MINUTES_PER_DAY = 1440;
export const ELAPSED_MINUTES_PER_WEEK = ELAPSED_MINUTES_PER_DAY * 7;
export const ELAPSED_MINUTES_PER_MONTH = ELAPSED_MINUTES_PER_DAY * 30;

const UNIT_TABLE: Readonly<Record<string, number>> = {
  m: 1,
  h: MINUTES_PER_HOUR,
  d: MINUTES_PER_DAY,
  w: MINUTES_PER_WEEK,
  mo: MINUTES_PER_MONTH,
  md: MINUTES_PER_DAY,
  eh: ELAPSED_MINUTES_PER_HOUR,
  ed: ELAPSED_MINUTES_PER_DAY,
  ew: ELAPSED_MINUTES_PER_WEEK,
  emo: ELAPSED_MINUTES_PER_MONTH,
};

/**
 * 中文工期單位對照(MS Project 中文版 / Excel 匯出常見寫法)。
 *
 * 注意:「工作日」是**工作天**,對應 `d`(480 分鐘),不是彈性天 `ed`。
 * 「日」「天」單獨出現時同樣視為工作天,與 MS Project 中文版顯示一致。
 */
const CHINESE_UNIT_TABLE: Readonly<Record<string, string>> = {
  工作日: 'd',
  工作天: 'd',
  個工作天: 'd',
  天: 'd',
  日: 'd',
  工作週: 'w',
  工作周: 'w',
  週: 'w',
  周: 'w',
  星期: 'w',
  個星期: 'w',
  個月: 'mo',
  月: 'mo',
  個工作月: 'mo',
  小時: 'h',
  時: 'h',
  分鐘: 'm',
  分: 'm',
};

// 單位段落用 `.*?` 擷取(而非只允許 ASCII 字母),才能吃到中文單位;
// 未知單位仍會在查表時落空並回傳 null,不會放行垃圾輸入。
const DURATION_PATTERN = /^\s*(\d+(?:\.\d+)?)\s*(.*?)\s*$/;

/**
 * 解析工期輸入字串為 Minutes。非法輸入回傳 null。
 */
export function parseDurationInput(input: string): Minutes | null {
  const match = DURATION_PATTERN.exec(input);
  if (!match) return null;

  const [, rawValue, rawUnit] = match;
  const value = Number(rawValue ?? '');
  if (!Number.isFinite(value)) return null;

  const rawUnitText = (rawUnit ?? '').trim();
  const unit = !rawUnitText
    ? 'd'
    : (CHINESE_UNIT_TABLE[rawUnitText] ?? rawUnitText.toLowerCase());
  const unitMinutes = UNIT_TABLE[unit];
  if (unitMinutes === undefined) return null;

  // 先做單位換算,再驗證是否溢位(合法但極大的輸入乘完可能變成 Infinity)。
  const result = value * unitMinutes;
  if (!Number.isFinite(result)) return null;

  return minutes(result);
}

/** 是否為彈性(elapsed)工期單位。 */
export function isElapsedUnit(unit: string): boolean {
  return unit.toLowerCase().startsWith('e') && unit.toLowerCase() in UNIT_TABLE;
}

/**
 * 將 Minutes 格式化為 `Nd` 顯示字串(以工作天為主要單位,取到小數點後兩位並去除多餘 0)。
 */
export function formatDuration(value: Minutes): string {
  const raw = value as unknown as number;
  if (raw === 0) return '0d';
  const days = raw / MINUTES_PER_DAY;
  const rounded = Math.round(days * 100) / 100;
  return `${rounded}d`;
}
